import type { Customer } from '@base/shared/modules/customers';
import { useQuery } from '@tanstack/react-query';
import { UserRound } from 'lucide-react';
import { useMemo } from 'react';
import { api, type Me } from '../../api.ts';
import { can } from '../../auth.tsx';
import i18n from '../../i18n/index.ts';
import type { NavArea } from '../../navigation.ts';

/** Quantos clientes aparecem como atalho no menu Vendas. */
const MENU_LIMIT = 50;

/**
 * Põe no departamento Vendas um atalho para cada cliente salvo (o nome do cliente), que abre a
 * janela com os dados dele em `/customers/<id>`. A lista acompanha o cadastro: salvar um cliente
 * atualiza `['customers']` e o menu se refaz.
 */
export function useCustomerMenu(areas: NavArea[], me: Me | null | undefined): NavArea[] {
  const allowed = !!me && can(me, 'customers.customer.read');
  const list = useQuery({
    queryKey: ['customers', 'menu'],
    queryFn: () => api.get<Customer[]>(`/customers?status=active&limit=${MENU_LIMIT}`),
    enabled: allowed,
  });
  const customers = list.data;
  return useMemo(() => {
    if (!customers || customers.length === 0) return areas;
    // O nome vira o texto do atalho (o menu lê tudo por chave de tradução).
    for (const c of customers) {
      const key = `customers.menu.${c.id}`;
      i18n.addResource('pt-BR', 'translation', key, c.name);
      i18n.addResource('en', 'translation', key, c.name);
    }
    return areas.map((area) => {
      if (area.id !== 'sales' || area.groups.length === 0) return area;
      const [first, ...rest] = area.groups;
      return {
        ...area,
        groups: [
          {
            ...first!,
            items: [
              ...first!.items,
              ...customers.map((c) => ({
                to: `/customers/${c.id}`,
                key: `customers.menu.${c.id}`,
                icon: UserRound,
                permission: null,
              })),
            ],
          },
          ...rest,
        ],
      };
    });
  }, [areas, customers]);
}
